// Not really sure whether to make this separate or not yet. Feels like a thing.
import { isDeletedUser } from "@shared";
import { allUsersStore, blockedUsersStore, serverBlockedUsersStore, suspendedUsersStore, webhookUserIdsStore, } from "./stores";
export class UsersState {
    #allUsers;
    #blockedUsers;
    #suspendedUsers;
    #specialUsers = new Set();
    // Moves on whenever the users change, since the map of them is updated in place
    #version = 0;
    constructor() {
        allUsersStore.subscribe((val) => {
            this.#allUsers = val;
            this.#version++;
        });
        blockedUsersStore.subscribe((val) => (this.#blockedUsers = val));
        suspendedUsersStore.subscribe((val) => (this.#suspendedUsers = val));
    }
    get version() {
        return this.#version;
    }
    setBlockedUsers(userIds) {
        serverBlockedUsersStore.set(new Set(userIds));
    }
    blockUser(userId) {
        serverBlockedUsersStore.update((users) => {
            users.add(userId);
            return new Set([...users]);
        });
    }
    unblockUser(userId) {
        serverBlockedUsersStore.update((users) => {
            if (users.has(userId)) {
                users.delete(userId);
                return new Set([...users]);
            }
            return users;
        });
    }
    addUser(user) {
        allUsersStore.update((map) => {
            map.set(user.userId, user);
            return map;
        });
        this.#updateSuspended([user]);
    }
    addMany(users) {
        if (users.length === 0)
            return;
        allUsersStore.update((map) => {
            users.forEach((u) => map.set(u.userId, u));
            return map;
        });
        this.#updateSuspended(users);
    }
    // Deleted users are left as they are, else they'd be requested again once this goes stale
    setUpdated(userIds, timestamp) {
        const toUpdate = userIds.filter((id) => {
            const user = allUsersStore.value.get(id);
            return user !== undefined && !isDeletedUser(user) && user.updated !== timestamp;
        });
        if (toUpdate.length === 0)
            return;
        allUsersStore.update((map) => {
            for (const userId of toUpdate) {
                const user = map.get(userId);
                if (user !== undefined) {
                    user.updated = timestamp;
                    map.set(userId, user);
                }
            }
            return map;
        });
    }
    userSuspended(userId, suspended) {
        const existing = allUsersStore.value.get(userId);
        if (existing === undefined || existing.suspended === suspended)
            return;
        allUsersStore.update((users) => {
            const u = users.get(userId);
            if (u) {
                u.suspended = suspended;
                users.set(userId, u);
                suspendedUsersStore.update((s) => {
                    if (suspended) {
                        s.add(userId);
                    }
                    else {
                        s.delete(userId);
                    }
                    return s;
                });
            }
            return users;
        });
    }
    setSpecialUsers(users) {
        this.#specialUsers = new Set(users.map((u) => u.userId));
        this.addMany(users);
    }
    get(userId) {
        return this.#allUsers.get(userId);
    }
    has(userId) {
        return this.#allUsers.has(userId);
    }
    addWebhookIds(webhooks) {
        const toAdd = webhooks.filter((id) => !webhookUserIdsStore.value.has(id));
        if (toAdd.length > 0) {
            webhookUserIdsStore.update((set) => {
                for (const webhook of webhooks) {
                    set.add(webhook);
                }
                return set;
            });
        }
    }
    updateUser(userId, updater) {
        const user = this.get(userId);
        if (user !== undefined) {
            const updated = updater(user);
            if (updated !== undefined) {
                this.addUser(updated);
            }
        }
    }
    get blockedUsers() {
        return this.#blockedUsers;
    }
    get allUsers() {
        return this.#allUsers;
    }
    get specialUsers() {
        return this.#specialUsers;
    }
    get suspendedUsers() {
        return this.#suspendedUsers;
    }
    #updateSuspended(users) {
        const toAdd = new Set();
        const toRemove = new Set();
        for (const user of users) {
            if (user.suspended) {
                if (!this.#suspendedUsers.has(user.userId)) {
                    toAdd.add(user.userId);
                }
            }
            else if (this.#suspendedUsers.has(user.userId)) {
                toRemove.add(user.userId);
            }
        }
        if (toAdd.size > 0 || toRemove.size > 0) {
            suspendedUsersStore.update((set) => {
                for (const userId of toAdd) {
                    set.add(userId);
                }
                for (const userId of toRemove) {
                    set.delete(userId);
                }
                return set;
            });
        }
    }
}
export const userStore = new UsersState();
